import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, InjectionToken, inject } from '@angular/core';
import {
  RUM_CONFIG,
  type RumClient,
  type RumClientFactory,
  type RumEnvironmentConfig,
} from './rum.config';
import {
  type TelemetryEventAttributes,
  type TelemetryEventName,
  sanitiseEvent,
} from './telemetry-events';
import { TelemetryConsentService } from './telemetry-consent.service';

export const RUM_CLIENT_FACTORY = new InjectionToken<RumClientFactory>('RUM_CLIENT_FACTORY', {
  providedIn: 'root',
  factory: () => async (config: RumEnvironmentConfig) => {
    const { AwsRum } = await import('aws-rum-web');
    return new AwsRum(config.appMonitorId, config.applicationVersion ?? '0.0.0', config.region, {
      sessionSampleRate: config.sessionSampleRate ?? RUM_CONFIG.defaultSessionSampleRate,
      // Resource-based policy on the app monitor keyed on the alias; requests are unsigned (see RumEnvironmentConfig).
      alias: config.alias,
      signing: false,
      allowCookies: RUM_CONFIG.defaultAllowCookies,
      disableAutoPageView: true,
      enableXRay: false,
      // Never send resource URLs: query strings can carry tokens or email addresses.
      recordResourceUrl: false,
      // No native 'http' telemetry: it records the full request URL of every failed call,
      // and some calls carry an id token or email in the query string. API calls are
      // recorded instead via the sanitised http_request event (see recordHttpRequest),
      // which links to the backend trace by our own traceparent id rather than X-Ray's.
      telemetries: ['errors', 'performance'],
    }) as RumClient;
  },
});

/** Environment RUM settings, e.g. { provide: RUM_ENV_CONFIG, useValue: environment.rum }. */
export const RUM_ENV_CONFIG = new InjectionToken<RumEnvironmentConfig | null>('RUM_ENV_CONFIG', {
  providedIn: 'root',
  factory: () => null,
});

/** Single entry point for RUM. No-op unless the flag is on and config is present. */
@Injectable({ providedIn: 'root' })
export class TelemetryService {
  private readonly features = inject<Record<string, boolean>>('features' as never, {
    optional: true,
  });
  private readonly config = inject(RUM_ENV_CONFIG);
  private readonly factory = inject(RUM_CLIENT_FACTORY);
  private readonly consent = inject(TelemetryConsentService);

  private client: RumClient | null = null;
  private starting: Promise<void> | null = null;
  // Events raised between start() and the lazy import resolving (e.g. the first API
  // failures during bootstrap) are replayed once the client exists rather than dropped:
  // the error_shown event is the only record of a browser-minted reference.
  private pending: Array<(client: RumClient) => void> = [];

  isEnabled(): boolean {
    return (
      this.features?.[RUM_CONFIG.featureFlag] === true &&
      !!this.config?.appMonitorId &&
      !!this.config.alias
    );
  }

  /** Creates the client (once) and applies the current consent. */
  start(): Promise<void> {
    if (!this.isEnabled() || !this.config) {
      return Promise.resolve();
    }
    this.starting ??= this.factory(this.config)
      .then((client) => {
        this.client = client;
        client.allowCookies(this.consent.granted);
        this.consent.changes$.subscribe((granted) => this.client?.allowCookies(granted));
        const queued = this.pending;
        this.pending = [];
        queued.forEach((send) => send(client));
      })
      .catch(() => {
        this.client = null;
        this.pending = [];
      });
    return this.starting;
  }

  recordPageView(routeTemplate: string): void {
    this.client?.recordPageView(routeTemplate);
  }

  recordEvent<K extends TelemetryEventName>(name: K, attributes: TelemetryEventAttributes[K]): void {
    const clean = sanitiseEvent(name, attributes);
    if (clean) {
      this.send((client) => client.recordEvent(name, clean));
    }
  }

  /** Records an error without URLs or response bodies. */
  recordError(error: unknown): void {
    let safe: Error;
    if (error instanceof HttpErrorResponse) {
      safe = new Error(`HttpError ${error.status}`);
    } else if (error instanceof Error) {
      safe = new Error(error.message.slice(0, RUM_CONFIG.maxErrorMessageLength));
      safe.name = error.name;
      safe.stack = error.stack;
    } else {
      safe = new Error('Unknown error');
    }
    this.send((client) => client.recordError(safe));
  }

  /** Sends now, or queues (bounded) while start() is still importing the client. */
  private send(action: (client: RumClient) => void): void {
    if (this.client) {
      action(this.client);
      return;
    }
    if (this.starting && this.pending.length < RUM_CONFIG.maxPendingEvents) {
      this.pending.push(action);
    }
  }

  /** Links an API call to its backend trace via the shared traceparent trace id. */
  recordHttpRequest(traceId: string, method: string, status: number, durationMs: number): void {
    this.recordEvent('http_request', {
      traceId,
      method: method.toUpperCase(),
      status,
      durationMs: Math.round(durationMs),
    });
  }
}
